import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { toast } from "sonner";
import { CalendarCheck } from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AddFollowUpModal({ 
  open, 
  onOpenChange, 
  customerId, 
  customerName,
  jobId,
  estimateId,
  invoiceId,
  linkedEntityTitle,
  onSuccess 
}) {
  const [dueDate, setDueDate] = useState(() => {
    // Default to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  });
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!note.trim()) {
      toast.error("Please enter a note");
      return;
    }
    
    if (!dueDate) {
      toast.error("Please select a due date");
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        customer_id: customerId,
        due_date: dueDate,
        note: note.trim(),
      };
      
      if (jobId) payload.job_id = jobId;
      if (estimateId) payload.estimate_id = estimateId;
      if (invoiceId) payload.invoice_id = invoiceId;
      
      const response = await fetch(`${API}/reminders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      
      if (response.ok) {
        toast.success("Follow-up created");
        setNote("");
        setDueDate(() => {
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          return tomorrow.toISOString().split('T')[0];
        });
        onOpenChange(false);
        if (onSuccess) onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create follow-up");
      }
    } catch (error) {
      toast.error("Failed to create follow-up");
    } finally {
      setSaving(false);
    }
  };

  const quickDates = [
    { label: "Tomorrow", days: 1 },
    { label: "3 Days", days: 3 },
    { label: "1 Week", days: 7 },
    { label: "2 Weeks", days: 14 },
    { label: "1 Month", days: 30 },
  ];

  const setQuickDate = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    setDueDate(date.toISOString().split('T')[0]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="add-followup-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarCheck size={20} className="text-[#FF6600]" />
            Add Follow-Up
          </DialogTitle>
          <DialogDescription>
            Schedule a reminder for {customerName}
            {linkedEntityTitle && <span className="text-zinc-600"> • {linkedEntityTitle}</span>}
          </DialogDescription>
        </DialogHeader>
        
        <DialogBody>
          <form id="add-followup-form" onSubmit={handleSubmit} className="space-y-4">
            {/* Quick Date Buttons */}
            <div>
              <Label className="text-xs text-zinc-500 mb-2 block">Quick Select</Label>
              <div className="flex flex-wrap gap-2">
                {quickDates.map((qd) => (
                  <Button
                    key={qd.days}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setQuickDate(qd.days)}
                    className="text-xs border-2"
                  >
                    {qd.label}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Date Picker */}
            <div>
              <Label htmlFor="due_date">Due Date</Label>
              <Input
                id="due_date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="border-2"
                required
                data-testid="followup-date-input"
              />
            </div>
            
            {/* Note */}
            <div>
              <Label htmlFor="note">Note</Label>
              <Textarea
                id="note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="What do you need to follow up on?"
                className="border-2 resize-none"
                rows={3}
                required
                data-testid="followup-note-input"
              />
            </div>
          </form>
        </DialogBody>
        
        <DialogFooter>
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            className="flex-1 sm:flex-none border-2"
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            form="add-followup-form"
            disabled={saving}
            className="flex-1 sm:flex-none bg-[#FF6600] hover:bg-[#CC5200] text-white"
            data-testid="save-followup-btn"
          >
            {saving ? "Saving..." : "Save Follow-Up"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
